import { Component, HostBinding } from '@angular/core';
import { Toast } from 'ngx-toastr';

/**
 * Custom Toast component that extends ngx-toastr with WCAG 4.1.3 compliant ARIA attributes.
 *
 * WCAG 4.1.3 Status Messages: Status messages must be programmatically determined through
 * role or properties such that they can be presented to the user by assistive technologies
 * without receiving focus.
 *
 * - Success/Info toasts: role="status" with aria-live="polite"
 * - Error/Warning toasts: role="alert" with aria-live="assertive"
 */
@Component({
  selector: '[accessible-toast-component]',
  template: `
    <button *ngIf="options.closeButton" (click)="remove()" type="button" class="toast-close-button" aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
    <div *ngIf="title" [class]="options.titleClass">
      {{ title }} <ng-container *ngIf="duplicatesCount">[{{ duplicatesCount }}]</ng-container>
    </div>
    <div *ngIf="message && options.enableHtml" [class]="options.messageClass" [innerHTML]="message">
    </div>
    <div *ngIf="message && !options.enableHtml" [class]="options.messageClass">
      {{ message }}
    </div>
    <div *ngIf="options.progressBar">
      <div class="toast-progress" [style.width]="width + '%'"></div>
    </div>
  `,
  styleUrls: ['./accessible-toast.component.scss'],
  preserveWhitespaces: false
})
export class AccessibleToastComponent extends Toast {
  /**
   * Binds ARIA role to host element based on toast type.
   * - 'alert' for errors and warnings (assertive)
   * - 'status' for success and info (polite)
   */
  @HostBinding('attr.role')
  get ariaRole(): string {
    const toastType = this.toastPackage.toastType;
    return toastType === 'toast-error' || toastType === 'toast-warning'
      ? 'alert'
      : 'status';
  }

  /**
   * Binds aria-live attribute to host element based on toast type.
   * - 'assertive' for errors and warnings (interrupts screen reader)
   * - 'polite' for success and info (waits for pause)
   */
  @HostBinding('attr.aria-live')
  get ariaLive(): 'assertive' | 'polite' {
    const toastType = this.toastPackage.toastType;
    return toastType === 'toast-error' || toastType === 'toast-warning'
      ? 'assertive'
      : 'polite';
  }

  /**
   * Ensures toast is atomic for screen readers (announced as a whole unit).
   */
  @HostBinding('attr.aria-atomic')
  get ariaAtomic(): string {
    return 'true';
  }
}
